import type { KyInstance } from 'ky'
import { isHTTPError } from 'ky'
import {
  paymentSystemIdByPlatform,
  waitForSubscriptionStatus,
  type SubscriptionServiceLevelStatus,
  type ServiceLevelStatus,
} from './subscribe'
import type { PaymentPlatform } from './types'

export type Transaction = {
  transaction_id: string
  product_type: 'subscription' | 'consumable' | 'non_consumable'
  sku: string
  purchased_at: string
}

export type Purchase = {
  id: number
  created_at: string
  iap_sku: string
  purchase_type: 'subscription' | (string & {})
}

export class RestoreNoTransactionError extends Error {
  constructor() {
    super('No transactions found')
    this.name = 'RestoreNoTransactionError'
  }
}

export class RestoreError extends Error {
  public status?: number
  constructor(message: string, status?: number) {
    super(message)
    this.name = 'RestoreError'
    this.status = status
  }
}

export interface RestoreSubscriptionOptions {
  api: { service: KyInstance; receipt: KyInstance }
  userId: number
  platform: PaymentPlatform
  getAllUnclosedTransactions: () => Promise<
    | { success: true; data: { transactions: Transaction[] } }
    | { success: false }
  >
  getAllEntitlements: () => Promise<
    | { success: true; data: { transactions: Transaction[] } }
    | { success: false }
  >
  closeTransaction: (transaction: { transaction_id: string }) => Promise<void>
  currentSubscriptionExpiresAt?: string | null
}

export async function restoreSubscription(options: RestoreSubscriptionOptions) {
  const {
    api,
    userId,
    platform,
    getAllUnclosedTransactions,
    getAllEntitlements,
    closeTransaction,
    currentSubscriptionExpiresAt,
  } = options

  const [transactions, entitlements] = await Promise.all([
    getAllUnclosedTransactions().then((result) => {
      if (!result.success) return []
      return result.data.transactions.filter(
        (transaction) => transaction.product_type === 'subscription',
      )
    }),
    getAllEntitlements().then((result) => {
      if (!result.success) return []
      return result.data.transactions.filter(
        (transaction) => transaction.product_type === 'subscription',
      )
    }),
  ])

  let transaction: Transaction
  if (transactions.length > 0) {
    const [latestTransaction] = transactions.sort(
      (a, b) =>
        new Date(b.purchased_at).getTime() - new Date(a.purchased_at).getTime(),
    )
    transaction = latestTransaction
  } else if (entitlements.length > 0) {
    const [latestEntitlement] = entitlements.sort(
      (a, b) =>
        new Date(b.purchased_at).getTime() - new Date(a.purchased_at).getTime(),
    )
    transaction = latestEntitlement
  } else {
    throw new RestoreNoTransactionError()
  }

  const { data: openedPurchases } = await api.service
    .get(`store/users/${userId}/purchase`, {
      searchParams: {
        status: 'open',
        page_size: 10,
        page: 1,
      },
    })
    .json<{ data: Purchase[] }>()

  const [openedPurchase] = openedPurchases
    .filter(
      (purchase) =>
        purchase.purchase_type ===
          (platform === 'china_android' ? 'iap' : 'subscription') &&
        purchase.iap_sku === transaction.sku,
    )
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )

  if (openedPurchase) {
    await api.service.post(
      `store/users/${userId}/purchase/${openedPurchase.id}/mark-processing`,
    )
  }

  let receiptRequestUrl = `projects/6/users/${userId}`
  let data = {}
  if (platform === 'china_android') {
    receiptRequestUrl += '/pending_iap_receipts'
    data = { receipt_data: transaction.transaction_id }
  } else {
    receiptRequestUrl += '/subscriptions'
    data = { transaction_id: transaction.transaction_id }
  }
  await api.receipt.post(receiptRequestUrl, {
    json: {
      ...data,
      sku_id: transaction.sku,
      payment_system_id: paymentSystemIdByPlatform[platform],
    },
  })

  await closeTransaction(transaction)

  try {
    const serviceLevelStatus = await waitForSubscriptionStatus({
      serviceApi: api.service,
      userId,
      shouldResolve: (status): status is SubscriptionServiceLevelStatus => {
        return (
          status.service_level === 'subscription' &&
          status.subscription.expires_at !== currentSubscriptionExpiresAt
        )
      },
    })
    return serviceLevelStatus
  } catch {
    return null
  }
}
